import { useEffect, useState } from 'react';
import { markRepliesSeen, myBugReports, replyKey, seenReplies, sendBugReport, type BugReport as Report } from '../lib/bugReports';
import { useClassroom } from '../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// "Report a bug": a tab on the right edge of every student page. It slides out
// a window where the student says what went wrong; the report goes to their
// teacher (routes/teacher/BugReports.tsx) with the page they were on.
// ─────────────────────────────────────────────────────────────────────────────

export default function BugReportTab() {
  const { active, session } = useClassroom();
  const me = session?.user.id ?? '';
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [mine, setMine] = useState<Report[]>([]);

  // Their reports (with any replies): checked on every page, so a new reply puts a dot on the tab.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!active || !me) return;
    myBugReports(active.id, me).then(rs => {
      setMine(rs);
      const seen = seenReplies();
      setUnread(rs.filter(r => r.reply && !seen.has(replyKey(r))).length);
    }).catch(() => setMine([]));
  }, [open, active, me, sent]);
  // Opening the window reads the replies.
  useEffect(() => {
    if (!open || !mine.length) return;
    markRepliesSeen(mine.filter(r => r.reply).map(replyKey));
    setUnread(0);
  }, [open, mine]);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);

  if (!active || !me) return null;

  const send = async () => {
    if (!text.trim()) { setError('Tell us what went wrong first.'); return; }
    setBusy(true); setError('');
    try { await sendBugReport(active.id, me, text); setText(''); setSent(true); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not send your report.'); }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button type="button" className="bug-tab" onClick={() => { setOpen(true); setSent(false); }} aria-label="Report a bug to your teacher">
          <span aria-hidden>🐞</span><span className="bug-tab-text">Report a bug</span>
          {unread > 0 && <span className="bug-dot" aria-label={`${unread} new repl${unread === 1 ? 'y' : 'ies'} from your teacher`}>{unread}</span>}
        </button>
      )}
      {open && (
        <div className="bug-backdrop" onClick={() => setOpen(false)}>
          <aside className="bug-panel" role="dialog" aria-modal="true" aria-label="Report a bug" onClick={e => e.stopPropagation()}>
            <div className="row"><h3>🐞 Report a bug</h3><span className="spacer" /><button type="button" className="icon" aria-label="Close" onClick={() => setOpen(false)}>✕</button></div>
            {sent ? (
              <div className="notice good">Thanks! Your teacher will see your report. 🛠️</div>
            ) : (
              <>
                <p className="small muted">Something not working? Tell your teacher what happened. Your report says which page you were on.</p>
                <label className="field">
                  <span>What went wrong?</span>
                  <textarea rows={5} maxLength={2000} value={text} onChange={e => setText(e.target.value)}
                    placeholder="When I tap a word in the story, it says “try again later”…" autoFocus />
                </label>
                <p className="small muted">Helpful things to say: what you were doing, what you expected, and what happened instead.</p>
                {error && <div className="notice bad" role="alert">{error}</div>}
                <button type="button" className="primary" disabled={busy || !text.trim()} onClick={send}>{busy ? 'Sending…' : 'Send to my teacher'}</button>
              </>
            )}
            {sent && <button type="button" onClick={() => setSent(false)}>Report something else</button>}
            {mine.length > 0 && (
              <div className="bug-mine">
                <h4>Your reports</h4>
                {mine.map(r => (
                  <div key={r.id} className="bug-mine-item">
                    <div className="bug-mine-row">
                      <span className={`bug-status ${r.status}`}>{r.status === 'fixed' ? '✅ Fixed' : '⏳ Sent'}</span>
                      <span className="small grow-text" dir="auto">{r.message.length > 90 ? r.message.slice(0, 90) + '…' : r.message}</span>
                    </div>
                    {r.reply && <div className="bug-reply small" dir="auto"><strong>Your teacher:</strong> {r.reply}</div>}
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
